import { useState, useEffect, useMemo } from 'react';
import { doc, collection, query, where, onSnapshot, addDoc, serverTimestamp, deleteDoc, updateDoc, increment, getDocs, writeBatch, runTransaction } from 'firebase/firestore';
import { db } from '../../../firebase';
import { invalidateCollections } from '../../../hooks/useCachedCollection';

export const useAccountStatement = (id) => {
    const [customer, setCustomer] = useState(null);
    const [serviceRecords, setServiceRecords] = useState([]);
    const [salesRecords, setSalesRecords] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [scheduledPayments, setScheduledPayments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const unsubCustomer = onSnapshot(doc(db, "customers", id), (docSnap) => {
            if (docSnap.exists()) { setCustomer({ id: docSnap.id, ...docSnap.data() }); }
        });
        const unsubServiceRecords = onSnapshot(query(collection(db, "serviceRecords"), where("musteriId", "==", id)), (snapshot) => {
            setServiceRecords(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        const unsubSalesRecords = onSnapshot(query(collection(db, "sales"), where("musteriId", "==", id)), (snapshot) => {
            setSalesRecords(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        const unsubTransactions = onSnapshot(query(collection(db, "transactions"), where("customerId", "==", id)), (snapshot) => {
            setTransactions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        const unsubScheduledPayments = onSnapshot(query(collection(db, "scheduledPayments"), where("musteriId", "==", id)), (snapshot) => {
            setScheduledPayments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });

        return () => {
            unsubCustomer();
            unsubServiceRecords();
            unsubSalesRecords();
            unsubTransactions();
            unsubScheduledPayments();
        };
    }, [id]);

    // Açık kayıtları hesapla (kalanTutar > 0 olan servis ve satış kayıtları)
    const openRecords = useMemo(() => {
        const records = [];

        serviceRecords.forEach(record => {
            const kalan = (record.toplamTutar || 0) - (record.odenenTutar || 0);
            if (kalan > 0) {
                records.push({
                    id: record.id,
                    type: 'service',
                    label: `Servis: #${record.serviceNo || record.cihazSeriNumarasi || record.id.substring(0, 6)}`,
                    description: record.cihazSeriNumarasi ? `Cihaz: ${record.cihazSeriNumarasi}` : '',
                    toplamTutar: record.toplamTutar || 0,
                    odenenTutar: record.odenenTutar || 0,
                    kalanTutar: kalan
                });
            }
        });

        salesRecords.forEach(record => {
            const kalan = (record.toplamTutar || 0) - (record.odenenTutar || 0);
            if (kalan > 0) {
                const urunListesi = (record.satilanUrunler || []).map(u => u.ad || u.urunAdi).join(', ');
                records.push({
                    id: record.id,
                    type: 'sale',
                    label: `Satış: #${record.id.substring(0, 6)}`,
                    description: urunListesi ? `Ürünler: ${urunListesi}` : '',
                    toplamTutar: record.toplamTutar || 0,
                    odenenTutar: record.odenenTutar || 0,
                    kalanTutar: kalan
                });
            }
        });

        return records;
    }, [serviceRecords, salesRecords]);

    const handleSaveGeneralPayment = async (paymentData) => {
        if (!customer) return;
        try {
            const selectedRecord = paymentData.selectedRecord;

            if (selectedRecord) {
                // Kayda bağlı tahsilat — Transaction ile atomik güncelleme
                await runTransaction(db, async (transaction) => {
                    const collectionName = selectedRecord.type === 'service' ? 'serviceRecords' : 'sales';
                    const recordRef = doc(db, collectionName, selectedRecord.id);
                    const recordDoc = await transaction.get(recordRef);

                    if (!recordDoc.exists()) {
                        throw new Error("İlgili kayıt bulunamadı!");
                    }

                    const recordData = recordDoc.data();
                    const newOdenen = (recordData.odenenTutar || 0) + paymentData.tutar;

                    // 1. Transaction kaydı oluştur (refId + refType ile)
                    const transactionRef = doc(collection(db, "transactions"));
                    const sharedId = transactionRef.id;

                    transaction.set(transactionRef, {
                        customerId: customer.id,
                        customerName: customer.adSoyad,
                        type: "tahsilat",
                        amount: paymentData.tutar,
                        paymentMethod: paymentData.odemeYontemi,
                        description: paymentData.aciklama || `Tahsilat: ${selectedRecord.label} (${paymentData.odemeYontemi})`,
                        timestamp: serverTimestamp(),
                        refId: selectedRecord.id,
                        refType: selectedRecord.type
                    });

                    // 2. İlgili kaydın odenenTutar ve kalanTutar güncelle
                    transaction.update(recordRef, {
                        odenenTutar: newOdenen,
                        kalanTutar: recordData.toplamTutar - newOdenen
                    });

                    // 3. Servis kaydıysa payments sub-collection'a da ekle
                    if (selectedRecord.type === 'service') {
                        const paymentRef = doc(recordRef, "payments", sharedId);
                        transaction.set(paymentRef, {
                            tutar: paymentData.tutar,
                            odemeYontemi: paymentData.odemeYontemi,
                            aciklama: paymentData.aciklama || `Cari Hesaptan Tahsilat (${paymentData.odemeYontemi})`,
                            tarih: serverTimestamp(),
                            musteriId: customer.id,
                            musteriAdSoyad: customer.adSoyad,
                            kaynakCariHesap: true
                        });
                    }

                    // 4. Müşteri bakiyesini güncelle
                    const customerRef = doc(db, "customers", customer.id);
                    transaction.update(customerRef, {
                        bakiye: increment(-paymentData.tutar)
                    });
                });

                alert('Tahsilat başarıyla kaydedildi ve ilgili kayda bağlandı.');
            } else {
                // Genel tahsilat (kayda bağlı değil)
                const batch = writeBatch(db);
                const transactionRef = doc(collection(db, "transactions"));

                batch.set(transactionRef, {
                    customerId: customer.id,
                    customerName: customer.adSoyad,
                    type: "tahsilat",
                    amount: paymentData.tutar,
                    paymentMethod: paymentData.odemeYontemi,
                    description: paymentData.aciklama || `Genel Tahsilat (${paymentData.odemeYontemi})`,
                    timestamp: serverTimestamp()
                });

                const customerRef = doc(db, "customers", customer.id);
                batch.update(customerRef, {
                    bakiye: increment(-paymentData.tutar)
                });

                await batch.commit();
                alert('Tahsilat başarıyla eklendi.');
            }
            // Cari liste bakiyeleri ve ilgili kayıt listeleri (cache okuyan ekranlar)
            // güncel kalsın diye ilgili koleksiyonları geçersiz kıl.
            await invalidateCollections(['customers', 'transactions', 'serviceRecords', 'sales']);
        } catch (error) {
            console.error("Tahsilat eklenirken hata: ", error);
            throw error;
        }
    };

    const handleSaveDebt = async (debtData) => {
        if (!customer) return;
        try {
            const batch = writeBatch(db);
            const transactionRef = doc(collection(db, "transactions"));

            batch.set(transactionRef, {
                customerId: customer.id,
                customerName: customer.adSoyad,
                type: "borc",
                amount: debtData.amount,
                description: debtData.description || "Genel Borçlandırma",
                timestamp: serverTimestamp()
            });

            const customerRef = doc(db, "customers", customer.id);
            batch.update(customerRef, {
                bakiye: increment(debtData.amount)
            });

            await batch.commit();
            await invalidateCollections(['customers', 'transactions']);
            alert('Borç kaydı başarıyla eklendi.');
        } catch (error) {
            console.error("Borç eklenirken hata: ", error);
            throw error;
        }
    };

    const handleDeleteTransaction = async (transaction) => {
        if (!transaction || !transaction.id) return;
        if (window.confirm("Bu finansal işlemi kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.")) {
            try {
                // Eğer transaction bir kayda bağlıysa, o kaydın finansal verilerini de geri al
                const transDoc = transactions.find(t => t.id === transaction.id);
                if (!transDoc) {
                    alert("İşlem bulunamadı.");
                    return;
                }

                const batch = writeBatch(db);
                const transactionRef = doc(db, "transactions", transaction.id);
                batch.delete(transactionRef);

                // Bakiyeyi tersine çevir
                const customerRef = doc(db, "customers", customer.id);
                const change = transDoc.type === 'borc' ? -transDoc.amount : transDoc.amount;
                batch.update(customerRef, {
                    bakiye: increment(change)
                });

                // Tahsilat siliniyorsa ve kayda bağlıysa, kaydın odenenTutar/kalanTutar geri al
                if (transDoc.type === 'tahsilat' && transDoc.refId && transDoc.refType) {
                    const collectionName = transDoc.refType === 'service' ? 'serviceRecords' : 'sales';
                    const recordRef = doc(db, collectionName, transDoc.refId);

                    // Alt koleksiyondan (payments) da sil
                    const paymentRef = doc(recordRef, "payments", transaction.id);
                    batch.delete(paymentRef);

                    // Kaydın mevcut verilerini kontrol et
                    try {
                        const recordSnapshot = await getDocs(query(collection(db, collectionName), where("__name__", "==", transDoc.refId)));
                        if (!recordSnapshot.empty) {
                            const recordData = recordSnapshot.docs[0].data();
                            const newOdenen = (recordData.odenenTutar || 0) - transDoc.amount;
                            batch.update(recordRef, {
                                odenenTutar: Math.max(0, newOdenen),
                                kalanTutar: recordData.toplamTutar - Math.max(0, newOdenen)
                            });
                        }
                    } catch (refError) {
                        console.warn("İlgili kayıt güncellenirken hata (kayıt silinmiş olabilir):", refError);
                    }
                }

                await batch.commit();
                await invalidateCollections(['customers', 'transactions', 'serviceRecords', 'sales']);
                alert("İşlem başarıyla silindi ve bakiye güncellendi.");
            } catch (error) {
                console.error("Finansal işlem silinirken hata:", error);
                alert("İşlem silinirken bir hata oluştu.");
            }
        }
    };

    const handleDeleteScheduledPayment = async (paymentId) => {
        if (window.confirm("Bu planlanmış tahsilatı kalıcı olarak silmek istediğinizden emin misiniz?")) {
            try {
                await deleteDoc(doc(db, "scheduledPayments", paymentId));
                alert("Planlanmış tahsilat başarıyla silindi.");
            } catch (error) {
                console.error("Planlanmış tahsilat silinirken hata:", error);
                alert("Kayıt silinirken bir hata oluştu.");
            }
        }
    };

    const allMovements = useMemo(() => {
        const movements = [
            ...serviceRecords.flatMap(record => ([
                { type: 'borc', date: record.olusturmaTarihi, description: `Servis Kaydı #${record.cihazSeriNumarasi}`, amount: record.toplamTutar, refId: record.id, isServiceRecord: true },
            ])),
            ...transactions.map(t => ({
                type: t.type === 'borc' ? 'borc' : 'alacak',
                date: t.timestamp,
                description: t.description,
                amount: t.amount,
                refId: t.id,
                refType: t.refType || null,
                linkedRefId: t.refId || null,
                isServiceRecord: false,
                transactionData: t
            })),
        ];

        movements.sort((a, b) => (a.date?.toMillis() || 0) - (b.date?.toMillis() || 0));
        let balance = 0;
        return movements.map(m => {
            balance += (m.type === 'borc' ? m.amount : -m.amount);
            return { ...m, balance };
        });
    }, [serviceRecords, transactions]);

    const pendingScheduledPayments = useMemo(() => {
        return scheduledPayments.filter(p => p.durum === 'Bekleniyor');
    }, [scheduledPayments]);

    const finalBalance = allMovements.length > 0 ? allMovements[allMovements.length - 1].balance : 0;

    // Bakiyeyi yeniden hesapla (Manuel Düzeltme için)
    const recalculateBalance = async () => {
        if (!customer) return;
        try {
            await updateDoc(doc(db, "customers", customer.id), {
                bakiye: finalBalance
            });
            await invalidateCollections(['customers']);
            alert("Bakiye yeniden hesaplandı ve güncellendi.");
        } catch (e) {
            console.error(e);
            alert("Hata oluştu.");
        }
    };

    return {
        customer,
        loading,
        allMovements,
        openRecords,
        pendingScheduledPayments,
        finalBalance,
        handleSaveGeneralPayment,
        handleSaveDebt,
        handleDeleteTransaction,
        handleDeleteScheduledPayment,
        recalculateBalance
    };
};
